<template>
  <form id="login" @submit.prevent="handleSubmit">
    <h1 class="title">Cadastro</h1>

    <register-steps class="my-4" :step="currentStep" :count="5" />

    <div class="alert -danger" v-if="showDangerAlert">
      <p class="title">Ops!</p>
      <p>{{ textDangerAlert }}</p>
    </div>

    <template v-if="!this.errors.unrecognized">
      <business-informations
        v-if="currentStep === 1"
        :form="formData"
        :errors="errors"
      />

      <user-informations
        v-if="currentStep === 2"
        :form="formData"
        :errors="errors"
      />

      <user-credentials
        v-if="currentStep === 3"
        :form="formData"
        :errors="errors"
      />

      <business-plan
        v-if="currentStep === 4"
        :plans="plans"
        v-model="formData.planId"
      />

      <creditcard-details
        ref="creditcardDetails"
        v-if="currentStep === 5"
        :plan="formData.planId"
        :form="formData"
        :errors="errors"
        @loading="loading = true"
        @loaded="loading = false"
      />

      <div class="actions">
        <button class="btn btn-primary mb-2" type="submit">
          {{ buttonLabel }}
        </button>

        <button v-if="currentStep > 1" class="btn btn-default text-sm" type="button" @click="currentStep -= 1">
          Voltar
        </button>
      </div>

      <div class="callSignIn" v-if="currentStep === 1">
        <p>Já possui cadastro? <router-link to="/login" class="btn-more-info">Fazer Login</router-link></p>
      </div>
    </template>

    <loader
      background="#FFF"
      v-if="loading"/>
  </form>
</template>

<script>
import checkErrorIsUnrecognizedResponse from '@/support/helpers/checkErrorIsUnrecognizedResponse';
import BusinessInformations from '@/auth/views/register/BusinessInformations.vue';
import CreditcardDetails from '@/auth/views/register/CreditcardDetails.vue';
import UserInformations from '@/auth/views/register/UserInformations.vue';
import UserCredentials from '@/auth/views/register/UserCredentials.vue';
import RegisterSteps from '@/auth/views/register/RegisterSteps.vue';
import BusinessPlan from '@/auth/views/register/BusinessPlan.vue';
import Loader from '@/support/components/Loader.vue';
import { mapState } from 'vuex';

export default {
  name: 'Register',

  data() {
    return {
      loading: true,
      currentStep: null,

      stepFields: {
        1: ['businessName', 'cnpj'],
        2: ['userName', 'cpf', 'phone'],
        3: ['email', 'password'],
        4: ['planId'],
        5: ['intentId', 'terms'],
      },

      formData: {
        businessName: '',
        cnpj: '',
        userName: '',
        cpf: '',
        phone: '',
        email: '',
        password: '',
        password_confirmation: '',
        planId: '',
        intentId: '',
        terms: '',
      },

      errors: {
        paymentError: null,
        unrecognized: false,
      },
    };
  },

  methods: {
    async handleSubmit() {
      try {
        this.loading = true;
        this.resetErrors();
        await this.register();
      } catch (err) {
        if (checkErrorIsUnrecognizedResponse(err, [422])) {
          this.errors.unrecognized = true;

          return;
        }

        const { data, status } = err.response;

        if (status === 422 && !this.stepHasErrors(data)) {
          this.currentStep += 1;
        }
      } finally {
        this.loading = false;
      }
    },

    async subscribe() {
      const stripeResponse = await this.$refs.creditcardDetails.subscribe();
      if (stripeResponse.error) {
        this.errors.paymentError = stripeResponse.error.message;
        return false;
      }

      this.formData.intentId = stripeResponse.paymentIntent.id;
      return true;
    },

    async register() {
      if (this.isLastStep && !(await this.subscribe())) {
        return;
      }

      await this.$store.dispatch('auth/register', this.parsedForm);

      if (this.isLastStep) {
        await this.$router.push({
          name: 'Auth-Login',
          params: { registered: true },
        });
      }
    },

    stepHasErrors(data) {
      let hasError = false;

      Object.values(this.stepFields[this.currentStep])
        .forEach((key) => {
          this.$set(this.errors, key, data.errors[key] || []);
          if (typeof data.errors[key] !== 'undefined') {
            hasError = true;
          }
        });

      return hasError;
    },

    initializeErrors() {
      Object.keys(this.formData)
        .forEach((key) => {
          this.$set(this.errors, key, []);
        });
    },

    /**
     * Reset the errors page.
     */
    resetErrors() {
      this.errors.unrecognized = false;

      if (!this.isLastStep) {
        this.errors.paymentError = null;
      }

      Object.keys(this.errors)
        .forEach((key) => {
          if (typeof this.errors[key] === 'object' && this.errors[key] !== null) {
            this.$set(this.errors, key, []);
          }
        });
    },
  },

  computed: {
    ...mapState('credits', {
      plans: 'plans',
      intent: 'intent',
    }),

    ...mapState('dashboard', [
      'dashboardConfig',
    ]),

    buttonLabel() {
      if (this.isLastStep) {
        return 'Assinar';
      }

      return 'Avançar';
    },

    parsedForm() {
      return {
        ...this.formData,
        cnpj: this.formData.cnpj.replace(/\D/g, ''),
        cpf: this.formData.cpf.replace(/\D/g, ''),
        validate: !this.isLastStep,
      };
    },

    /**
     * Check if can show the danger alert. Will be true if errors.unrecognized
     * is true.
     *
     * @returns {boolean}
     */
    showDangerAlert() {
      if (this.errors.unrecognized) {
        return true;
      }

      if (this.errors.paymentError !== null) {
        return true;
      }

      return false;
    },

    /**
     * The text to show in the danger alert.
     *
     * @returns {string}
     */
    textDangerAlert() {
      if (this.errors.paymentError !== null) {
        return this.errors.paymentError;
      }

      if (this.errors.unrecognized) {
        return 'Ocorreu um erro ao tentar fazer cadastro, tente novamente';
      }

      return 'Erro desconhecido';
    },

    /**
     * Check if is the last step.
     *
     * @returns {boolean}
     */
    isLastStep() {
      return this.currentStep === 5;
    },
  },

  async mounted() {
    await this.$store.dispatch('credits/registerPlans', {
      group: this.dashboardConfig.group,
    });

    this.initializeErrors();
    this.currentStep = 1;
    this.loading = false;
  },

  components: {
    Loader,
    BusinessPlan,
    RegisterSteps,
    UserCredentials,
    UserInformations,
    CreditcardDetails,
    BusinessInformations,
  },
};
</script>

<style lang="scss" scoped>
  #login {
    @apply relative;

    > .alert {
      @apply mb-4;
    }

    > .title {
      @apply text-xl;
      @apply font-bold;
      @apply text-secondary-700;
      letter-spacing: -0.01em;
      margin-bottom: 8px;
    }

    > .subtitle {
      @apply text-sm;
      @apply mb-5;
      @apply text-gray-800;
    }

    > .form-group {
      @apply mb-5;
    }

    > .actions {
      @apply mb-5;

      > .btn {
        @apply w-full;
      }
    }

    > .callSignIn {
      @apply text-sm;
      @apply mb-5;
      @apply text-gray-800;
    }
  }
</style>
